import { Add01Icon, Remove01Icon } from '@hugeicons/core-free-icons';
import type { TicketType } from '@sunrise-ui/api/events';
import {
  Colors,
  ExpandableText,
  IconButton,
  TextBold,
  TextReg,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import React from 'react';
import { StyleSheet, View } from 'react-native';

type Props = {
  ticketType: TicketType & { id: string };
  quantity: number;
  onIncrease: () => void;
  onDecrease: () => void;
};

export function TicketCard({
  ticketType,
  quantity,
  onIncrease,
  onDecrease,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const {
    name,
    sale_start_date,
    sale_end_date,
    description,
    price,
    is_active,
  } = ticketType;

  // Determine sale date text
  const now = DateTime.now();
  let isOnSale = true;
  let saleDateText = null;

  if (sale_start_date) {
    const startDate = DateTime.fromISO(sale_start_date);
    if (startDate > now) {
      isOnSale = false;
      saleDateText = `Sale starts ${startDate.toLocaleString(DateTime.DATE_SHORT)}`;
    }
  } else if (sale_end_date) {
    const endDate = DateTime.fromISO(sale_end_date);
    if (endDate > now) {
      saleDateText = `Sale ends ${endDate.toLocaleString(DateTime.DATE_SHORT)}`;
    }
  }

  const disabled = !is_active || !isOnSale;
  const isFree = price.toFixed(2) === '0.00';

  return (
    <View style={[styles.ticketCard, disabled && styles.ticketCardDisabled]}>
      <View style={styles.ticketCardLeft}>
        <TextBold style={styles.ticketName}>{name}</TextBold>
        {saleDateText && (
          <TextReg style={styles.ticketSales}>{saleDateText}</TextReg>
        )}
        {description && <ExpandableText text={description} charLimit={50} />}
      </View>

      <View style={styles.ticketCardRight}>
        <View style={styles.quantityControls}>
          <IconButton
            icon={Remove01Icon}
            onPress={onDecrease}
            disabled={disabled || quantity < 1}
            backgroundColor={
              disabled || quantity < 1 ? Colors.opacity20 : Colors.purple1
            }
            size="small"
          />
          <TextBold style={styles.quantityText}>{quantity}</TextBold>
          <IconButton
            icon={Add01Icon}
            backgroundColor={disabled ? Colors.opacity20 : Colors.purple1}
            onPress={onIncrease}
            disabled={disabled}
            size="small"
          />
        </View>
        <TextBold style={styles.ticketPrice}>
          {isFree ? 'Free' : `$${price.toFixed(2)}`}
        </TextBold>
      </View>
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    ticketCard: {
      backgroundColor: Colors.opacity05,
      borderRadius: 16,
      padding: isMobile ? 12 : 16,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 12,
    },
    ticketCardDisabled: {
      opacity: 0.5,
    },
    ticketCardLeft: {
      flex: 1,
      gap: 4,
    },
    ticketName: {
      fontSize: isMobile ? 15 : 16,
      color: Colors.text,
    },
    ticketSales: {
      fontSize: isMobile ? 11 : 12,
      color: Colors.placeholderText,
    },
    ticketPrice: {
      fontSize: isMobile ? 14 : 15,
      color: Colors.text,
      marginTop: 8,
    },
    ticketCardRight: {
      alignItems: 'center',
      gap: 4,
    },
    quantityControls: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    quantityText: {
      fontSize: isMobile ? 16 : 18,
      color: Colors.text,
      minWidth: 30,
      textAlign: 'center',
    },
  });
